/**
 * O formulário de disponibilidade, do lado da coordenação. P12.
 *
 * Substitui o questionário genérico que volta com 306 respostas para
 * redigitar e conferir (§14.4): aqui cada professor recebe um link dele, que
 * já diz o que ele precisa cobrir, e a resposta cai direto no cadastro. A
 * tela mostra quem tem link, quem respondeu e — o que interessa — **quem
 * respondeu sem cobrir**, com a explicação dele.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { ErroApi, formularios, type FormularioDoProfessor, type LinkDeFormulario } from '../api'
import { type Registro, rotulosDistintos } from '../rotulos'

type Filtro = 'TODOS' | 'NAO_COBRE' | 'SEM_RESPOSTA' | 'SEM_LINK'

const ESTADO: Record<FormularioDoProfessor['estado'], string> = {
  SEM_LINK: 'sem link',
  ENVIADO: 'enviado',
  RESPONDIDO: 'respondido',
  VENCIDO: 'venceu sem resposta',
}

const dia = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''

export function Formularios({ projeto }: { projeto: string }) {
  const [lista, setLista] = useState<FormularioDoProfessor[]>([])
  const [tempos, setTempos] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('TODOS')
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set())
  const [links, setLinks] = useState<LinkDeFormulario[] | null>(null)
  const [gerando, setGerando] = useState(false)
  const [copiado, setCopiado] = useState(false)

  const carregar = useCallback(async () => {
    try {
      const r = await formularios.listar(projeto)
      setLista(r.professores)
      setTempos(r.tempos)
      setErro(null)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar')
    }
  }, [projeto])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const rotulos = useMemo(() => rotulosDistintos(lista as unknown as Registro[]), [lista])

  const visiveis = lista.filter((p) =>
    filtro === 'NAO_COBRE'
      ? p.com_carga && p.faltas.length > 0
      : filtro === 'SEM_RESPOSTA'
        ? p.estado === 'ENVIADO' || p.estado === 'VENCIDO'
        : filtro === 'SEM_LINK'
          ? p.estado === 'SEM_LINK'
          : true,
  )
  const contar = (f: Filtro) =>
    lista.filter((p) =>
      f === 'NAO_COBRE'
        ? p.com_carga && p.faltas.length > 0
        : f === 'SEM_RESPOSTA'
          ? p.estado === 'ENVIADO' || p.estado === 'VENCIDO'
          : f === 'SEM_LINK'
            ? p.estado === 'SEM_LINK'
            : true,
    ).length

  const todosVisiveisEscolhidos = visiveis.length > 0 && visiveis.every((p) => escolhidos.has(p.id))

  const gerar = async () => {
    setGerando(true)
    try {
      const novos = await formularios.gerar(projeto, [...escolhidos])
      setLinks(novos)
      setCopiado(false)
      setEscolhidos(new Set())
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível gerar os links')
    } finally {
      setGerando(false)
    }
  }

  const endereco = (token: string) => `${location.origin}/?formulario=${encodeURIComponent(token)}`

  // Uma linha por professor, separada por tabulação: cola direto numa
  // planilha ou numa mala direta. O aviso por e-mail é o P11.
  const copiarTudo = async () => {
    if (!links) return
    const texto = links
      .map((l) => [rotulos.get(l.professor_id) ?? l.nome, l.email ?? '', endereco(l.token)].join('\t'))
      .join('\n')
    await navigator.clipboard.writeText(texto)
    setCopiado(true)
  }

  return (
    <div className="section">
      <header className="page-head">
        <h1>Formulário de disponibilidade</h1>
        <p className="page-head__note">
          Um link para cada professor. O formulário diz quantas aulas ele precisa cobrir em cada turno e em que dia —{' '}
          <strong>sem dizer de que disciplina ou turma</strong>. A resposta entra direto na disponibilidade dele.
        </p>
      </header>

      {erro && (
        <p className="state state--pendencia" role="alert">
          {erro}
        </p>
      )}
      {tempos === 0 && lista.length > 0 && (
        <p className="callout">
          Ainda não há horários de aula nos esquemas. O formulário é feito deles — cadastre os esquemas antes de gerar
          os links.
        </p>
      )}

      {links && (
        <section className="section stack" data-tour="links">
          <div className="section__head">
            <h2>Links gerados agora</h2>
            <p className="field__hint">
              Copie agora: por segurança, o sistema guarda só uma impressão do link e não consegue mostrá-lo de novo.
              Gerar outro para o mesmo professor invalida este.
            </p>
          </div>
          <div className="row">
            <button type="button" className="btn btn--primary" onClick={() => void copiarTudo()}>
              Copiar todos ({links.length})
            </button>
            {copiado && (
              <span className="state state--ok" role="status">
                Copiado: nome, e-mail e link, uma linha por professor.
              </span>
            )}
            <button type="button" className="btn btn--ghost" onClick={() => setLinks(null)}>
              Fechar
            </button>
          </div>
          <div className="tabela-rolavel">
            <table className="tabela">
              <tbody>
                {links.map((l) => (
                  <tr key={l.professor_id}>
                    <td>{rotulos.get(l.professor_id) ?? l.nome}</td>
                    <td>{l.email ?? <span className="field__hint">sem e-mail</span>}</td>
                    <td className="formularios__links">{endereco(l.token)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="row">
        <div className="segmented segmented--texto" role="group" aria-label="Filtrar professores" data-tour="filtros">
          {(
            [
              ['TODOS', 'Todos'],
              ['NAO_COBRE', 'Não cobrem a carga'],
              ['SEM_RESPOSTA', 'Sem resposta'],
              ['SEM_LINK', 'Sem link'],
            ] as const
          ).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              className="segmented__option"
              aria-pressed={filtro === id}
              onClick={() => setFiltro(id)}
            >
              {rotulo} <span className="mono">{contar(id)}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn--primary"
          data-tour="gerar"
          disabled={escolhidos.size === 0 || gerando || tempos === 0}
          onClick={() => void gerar()}
        >
          {gerando ? 'Gerando…' : `Gerar link para ${escolhidos.size} professor(es)`}
        </button>
      </div>

      <div className="tabela-rolavel">
        <table className="tabela" data-tour="lista">
          <thead>
            <tr>
              <th scope="col">
                <input
                  type="checkbox"
                  aria-label="Escolher todos os da lista"
                  checked={todosVisiveisEscolhidos}
                  onChange={() =>
                    setEscolhidos((atual) => {
                      const novo = new Set(atual)
                      for (const p of visiveis) {
                        if (todosVisiveisEscolhidos) novo.delete(p.id)
                        else novo.add(p.id)
                      }
                      return novo
                    })
                  }
                />
              </th>
              <th scope="col">Professor</th>
              <th scope="col">O que o formulário pede</th>
              <th scope="col">Formulário</th>
              <th scope="col">Disponibilidade de hoje</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((p) => (
              <tr key={p.id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Escolher ${rotulos.get(p.id) ?? p.nome}`}
                    checked={escolhidos.has(p.id)}
                    onChange={() =>
                      setEscolhidos((atual) => {
                        const novo = new Set(atual)
                        if (novo.has(p.id)) novo.delete(p.id)
                        else novo.add(p.id)
                        return novo
                      })
                    }
                  />
                </td>
                <td>
                  {rotulos.get(p.id) ?? p.nome}
                  {!p.email && <div className="field__hint">sem e-mail</div>}
                </td>
                <td>
                  {p.com_carga ? (
                    p.pedido.join('; ')
                  ) : (
                    <span className="field__hint">sem carga — recebe o formulário aberto, sem números</span>
                  )}
                </td>
                <td>
                  {ESTADO[p.estado]}
                  {p.estado === 'ENVIADO' && <div className="field__hint">vale até {dia(p.expira_em)}</div>}
                  {p.estado === 'RESPONDIDO' && <div className="field__hint">em {dia(p.respondido_em)}</div>}
                  {p.estado !== 'SEM_LINK' && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={async () => {
                        await formularios.revogar(projeto, p.id)
                        await carregar()
                      }}
                    >
                      Invalidar link
                    </button>
                  )}
                </td>
                <td>
                  {!p.com_carga ? (
                    ''
                  ) : p.faltas.length === 0 ? (
                    <span className="state state--ok">cobre</span>
                  ) : (
                    <>
                      <ul className="state state--pendencia">
                        {p.faltas.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                      {p.justificativa && <p className="field__hint">Explicação: “{p.justificativa}”</p>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
